import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ArrowRight, Camera, MousePointer2, Target, Sparkles, Users } from 'lucide-react';
import SmileDesignLogo from './SmileDesignLogo';

// ============================================================================
// Types & Data
// ============================================================================

interface OnboardingStep {
    id: number;
    title: string;
    subtitle: string;
    description: string;
    features: string[];
    image?: string;
    beforeImage?: string;
    afterImage?: string;
    video?: string;
    icon: React.ReactNode;
    mediaType: 'image' | 'slider' | 'video';
}

const STORAGE_KEY_COMPLETE = 'futurasmile_onboarding_complete';
const STORAGE_KEY_STEP = 'futurasmile_onboarding_step';

const ONBOARDING_STEPS: OnboardingStep[] = [
    {
        id: 1,
        title: 'Capture the Perfect Smile',
        subtitle: 'Image Acquisition',
        description: 'Upload a high-quality photo showing the patient\'s natural smile. This forms the foundation for AI-powered simulations.',
        features: ['Teeth fully visible', 'Natural lighting', 'Front-facing angle', 'High resolution'],
        image: '/images/onboarding/step1-smile.png',
        icon: <Camera className="w-5 h-5" />,
        mediaType: 'image'
    },
    {
        id: 2,
        title: 'Define the Mouth Area',
        subtitle: 'Precision Selection',
        description: 'Use the point-based selection tool to outline the mouth region. The AI focuses precisely on this treatment area.',
        features: ['Point-by-point selection', 'Auto-refinement', 'Real-time preview', 'Clinical precision'],
        image: '/images/onboarding/step2-mouth.gif',
        icon: <MousePointer2 className="w-5 h-5" />,
        mediaType: 'image'
    },
    {
        id: 3,
        title: 'Choose Treatment Goal',
        subtitle: 'Clinical Intent',
        description: 'Select the treatment type. Each option activates specialized AI logic for that specific clinical outcome.',
        features: ['Cosmetic enhancement', 'Restorative work', 'Orthodontic correction', 'Custom protocols'],
        image: '/images/onboarding/step3-goals.png',
        icon: <Target className="w-5 h-5" />,
        mediaType: 'image'
    },
    {
        id: 4,
        title: 'Generate AI Predictions',
        subtitle: 'Intelligent Simulation',
        description: 'Our AI creates ultra-realistic simulations that respect anatomy, biology, and natural aesthetics.',
        features: ['Clinically achievable', 'Natural morphology', 'Multiple variations', 'Dental standards'],
        beforeImage: '/images/onboarding/step4-before.png',
        afterImage: '/images/onboarding/step4-after.png',
        icon: <Sparkles className="w-5 h-5" />,
        mediaType: 'slider'
    },
    {
        id: 5,
        title: 'Engage Your Patients',
        subtitle: 'Case Acceptance',
        description: 'Transform simulations into confidence. Use visuals to educate and improve treatment acceptance.',
        features: ['Before/after views', 'PDF reports', 'Video presentations', 'Higher acceptance'],
        video: '/images/onboarding/step5-demo.mp4',
        icon: <Users className="w-5 h-5" />,
        mediaType: 'video'
    }
];

// ============================================================================
// Before/After Slider Component
// ============================================================================

interface BeforeAfterSliderProps {
    beforeImage: string;
    afterImage: string;
}

const BeforeAfterSlider: React.FC<BeforeAfterSliderProps> = ({ beforeImage, afterImage }) => {
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMove = useCallback((clientX: number) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
        setSliderPosition(percentage);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) handleMove(e.clientX);
    }, [isDragging, handleMove]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (isDragging && e.touches[0]) handleMove(e.touches[0].clientX);
    }, [isDragging, handleMove]);

    const handleMouseUp = useCallback(() => setIsDragging(false), []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

    return (
        <div
            ref={containerRef}
            className="relative w-full aspect-square cursor-ew-resize select-none overflow-hidden"
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
        >
            {/* After Image (Background) */}
            <img
                src={afterImage}
                alt="After"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
            />

            {/* Before Image (Clipped) */}
            <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
            >
                <img
                    src={beforeImage}
                    alt="Before"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ width: `${100 / (sliderPosition / 100)}%`, maxWidth: 'none' }}
                    draggable={false}
                />
            </div>

            {/* Slider Line */}
            <div
                className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
                {/* Slider Handle */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
                    <div className="flex items-center gap-0.5">
                        <ChevronLeft className="w-4 h-4 text-zinc-600" />
                        <ChevronLeft className="w-4 h-4 text-zinc-600 rotate-180 -ml-2" />
                    </div>
                </div>
            </div>

            {/* Labels */}
            <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
                <span className="text-xs font-medium text-white">Before</span>
            </div>
            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
                <span className="text-xs font-medium text-white">After</span>
            </div>
        </div>
    );
};

// ============================================================================
// Video Player Component
// ============================================================================

interface VideoPlayerProps {
    src: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.play().catch(() => {
                // Autoplay might be blocked, that's ok
            });
        }
    }, [src]);

    return (
        <div className="relative w-full aspect-square overflow-hidden bg-zinc-900">
            <video
                ref={videoRef}
                src={src}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Play indicator */}
            <div className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs font-medium text-white">Playing</span>
            </div>
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

interface OnboardingModalProps {
    onComplete: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [direction, setDirection] = useState(1);

    useEffect(() => {
        const isComplete = localStorage.getItem(STORAGE_KEY_COMPLETE) === 'true';
        if (!isComplete) {
            const savedStep = localStorage.getItem(STORAGE_KEY_STEP);
            if (savedStep) {
                const step = parseInt(savedStep, 10);
                if (step >= 0 && step < ONBOARDING_STEPS.length) setCurrentStep(step);
            }
            setIsVisible(true);
        }
    }, []);

    useEffect(() => {
        if (isVisible) localStorage.setItem(STORAGE_KEY_STEP, currentStep.toString());
    }, [currentStep, isVisible]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isVisible) return;
            if (e.key === 'Escape') handleSkip();
            else if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
            else if (e.key === 'ArrowLeft') handlePrevious();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isVisible, currentStep]);

    const handleNext = useCallback(() => {
        setImageLoaded(false);
        setDirection(1);
        if (currentStep < ONBOARDING_STEPS.length - 1) setCurrentStep(prev => prev + 1);
        else handleComplete();
    }, [currentStep]);

    const handlePrevious = useCallback(() => {
        setImageLoaded(false);
        setDirection(-1);
        if (currentStep > 0) setCurrentStep(prev => prev - 1);
    }, [currentStep]);

    const handleComplete = useCallback(() => {
        localStorage.setItem(STORAGE_KEY_COMPLETE, 'true');
        localStorage.removeItem(STORAGE_KEY_STEP);
        setIsVisible(false);
        onComplete();
    }, [onComplete]);

    const handleSkip = useCallback(() => {
        localStorage.setItem(STORAGE_KEY_COMPLETE, 'true');
        localStorage.removeItem(STORAGE_KEY_STEP);
        setIsVisible(false);
        onComplete();
    }, [onComplete]);

    const step = ONBOARDING_STEPS[currentStep];
    const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

    // Render media based on type
    const renderMedia = () => {
        switch (step.mediaType) {
            case 'slider':
                return (
                    <BeforeAfterSlider
                        beforeImage={step.beforeImage!}
                        afterImage={step.afterImage!}
                    />
                );
            case 'video':
                return <VideoPlayer src={step.video!} />;
            case 'image':
            default:
                return (
                    <>
                        <img
                            src={step.image}
                            alt={step.title}
                            onLoad={() => setImageLoaded(true)}
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                                }`}
                        />
                        {/* Left edge gradient */}
                        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0A0A0B] to-transparent" />
                    </>
                );
        }
    };

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
                    role="dialog"
                    aria-modal="true"
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/95" onClick={handleSkip} />

                    {/* Subtle ambient glow */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-500/[0.03] rounded-full blur-[150px]" />
                    </div>

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                        className="relative w-full max-w-[1200px] h-[min(720px,88vh)] bg-[#0A0A0B] rounded-3xl overflow-hidden shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_20px_50px_-10px_rgba(0,0,0,0.7)]"
                    >
                        {/* Subtle top border gradient */}
                        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                        {/* Layout Grid */}
                        <div className="h-full grid grid-cols-1 lg:grid-cols-2">

                            {/* Left Panel: Content */}
                            <div className="relative flex flex-col h-full p-8 lg:p-10 xl:p-12">

                                {/* Header */}
                                <div className="flex items-center justify-between mb-10">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 flex items-center justify-center">
                                            <SmileDesignLogo />
                                        </div>
                                        <img
                                            src="/images/Logo.webp"
                                            alt="FuturaSmile"
                                            className="h-6 w-auto object-contain"
                                        />
                                    </div>
                                    <button
                                        onClick={handleSkip}
                                        className="p-2 -mr-2 rounded-lg text-zinc-600 hover:text-zinc-400 hover:bg-white/5 transition-all duration-200"
                                        aria-label="Close"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="flex-1 flex flex-col justify-center -mt-8">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={currentStep}
                                            initial={{ opacity: 0, x: direction > 0 ? 30 : -30 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: direction > 0 ? -30 : 30 }}
                                            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                                        >
                                            {/* Badge */}
                                            <div className="inline-flex items-center gap-2.5 mb-5">
                                                <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
                                                    {step.icon}
                                                </div>
                                                <span className="text-xs font-semibold text-teal-400 uppercase tracking-[0.15em]">
                                                    {step.subtitle}
                                                </span>
                                            </div>

                                            {/* Title */}
                                            <h2 className="text-[2rem] lg:text-[2.5rem] xl:text-[2.75rem] font-semibold text-white tracking-[-0.025em] leading-[1.1] mb-5">
                                                {step.title}
                                            </h2>

                                            {/* Description */}
                                            <p className="text-base lg:text-lg text-zinc-400 leading-relaxed mb-8 max-w-lg">
                                                {step.description}
                                            </p>

                                            {/* Features */}
                                            <div className="grid grid-cols-2 gap-3">
                                                {step.features.map((feature, i) => (
                                                    <motion.div
                                                        key={i}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 0.1 + i * 0.05, duration: 0.3 }}
                                                        className="flex items-center gap-2.5 px-4 py-3 bg-white/[0.02] border border-white/[0.04] rounded-xl"
                                                    >
                                                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                                                        <span className="text-sm text-zinc-300">{feature}</span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>

                                {/* Footer */}
                                <div className="pt-6 mt-auto border-t border-white/[0.04]">
                                    {/* Progress */}
                                    <div className="flex items-center gap-1.5 mb-6">
                                        {ONBOARDING_STEPS.map((_, i) => (
                                            <button
                                                key={i}
                                                onClick={() => {
                                                    setImageLoaded(false);
                                                    setDirection(i > currentStep ? 1 : -1);
                                                    setCurrentStep(i);
                                                }}
                                                className={`h-1 rounded-full transition-all duration-500 ${i === currentStep
                                                    ? 'flex-[3] bg-teal-500'
                                                    : i < currentStep
                                                        ? 'flex-1 bg-teal-500/30'
                                                        : 'flex-1 bg-zinc-800'
                                                    }`}
                                                aria-label={`Go to step ${i + 1}`}
                                            />
                                        ))}
                                    </div>

                                    {/* Buttons */}
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={handlePrevious}
                                            disabled={currentStep === 0}
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-zinc-500 hover:text-white rounded-xl transition-colors disabled:opacity-0 disabled:pointer-events-none"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            Back
                                        </button>

                                        <div className="flex-1" />

                                        <button
                                            onClick={handleSkip}
                                            className="px-4 py-2.5 text-sm font-medium text-zinc-600 hover:text-zinc-400 transition-colors"
                                        >
                                            Skip tutorial
                                        </button>

                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleNext}
                                            className="flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-sm font-semibold text-white rounded-xl shadow-lg shadow-teal-500/20 transition-all duration-200"
                                        >
                                            {isLastStep ? 'Get Started' : 'Continue'}
                                            <ArrowRight className="w-4 h-4" />
                                        </motion.button>
                                    </div>
                                </div>
                            </div>

                            {/* Right Panel: Media */}
                            <div className="relative hidden lg:flex items-center justify-center bg-zinc-900/50 p-8">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentStep}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                                        className="relative w-full max-w-[480px] aspect-square rounded-2xl overflow-hidden shadow-2xl border border-white/5"
                                    >
                                        {renderMedia()}

                                        {/* Step counter */}
                                        {step.mediaType === 'image' && (
                                            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full border border-white/10">
                                                <span className="text-xs font-medium text-white/80">
                                                    {step.id} of {ONBOARDING_STEPS.length}
                                                </span>
                                            </div>
                                        )}
                                    </motion.div>
                                </AnimatePresence>

                                {/* Loading state for images */}
                                {step.mediaType === 'image' && !imageLoaded && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-10 h-10 border-2 border-zinc-800 border-t-teal-500 rounded-full animate-spin" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default OnboardingModal;
